// Časová osa doručení v nákupním boxu detailu (design_handoff_product/README.md
// §2 „Nákupní box" a §Interakce): Objednáte → Odešleme → Doručíme + countdown
// do expedice.
//
// Zdroj pravdy pro DEN DORUČENÍ je nativní `div.delivery-time` (Shoptet ho
// počítá z konfigurace dostupností, viz CLAUDE.md „Termín doručení") — stejná
// zásada jako v pokladně (checkoutPage.js). Prototyp si den doručení počítal sám
// (expedice + 1 pracovní den); my ho bereme nativní a počítáme jen kroky
// „Objednáte"/„Odešleme" podle uzávěrky expedice (`dispatchEta.js`: 15:00, v pátek
// 14:30, včetně svátků — rozhodnutí z redesignu výpisu). Bez nativního data padá i „Doručíme" na výpočet.
//
// Čistá funkce kvůli testům (tools/devcheck/product-detail.mjs): `now` i nativní
// datum jdou parametrem.
//
// Zvláštní závozy dopravců mimo rytmus skladu (`t.fast`). Shoptet zná jeden
// termín pro celý košík a checkbox „Doručení v sobotu" (/admin/zobrazeni-zbozi/)
// zůstává VYPNUTÝ — zapnutý by sobotu slíbil i za PPL. Rychlejší termín proto
// počítá šablona a ukazuje ho JEN u metod, jejichž název sedí na `re` pravidla;
// ostatní drží nativní termín. Dvě pravidla (`FAST_RULES`), okna se nepřekrývají,
// takže v jednu chvíli platí nejvýš jedno:
//
// • GLS SOBOTA (zadání Tomáše 2026-09-02): GLS doručuje i v sobotu, balík
//   předaný v pátek je u zákazníka následující den. Okno = čtvrtek po cut-offu
//   až pátek do cut-offu (expedice v pátek).
// • PPL NEDĚLE (zadání Tomáše 2026-09-05): PPL dělá pro klienta nedělní svozy —
//   objednávku přes PPL do SOBOTNÍ PŮLNOCI odešleme v neděli a PPL ji doručí
//   v pondělí (ostatní dopravci až v úterý). Okno = od pátečního cut-offu do
//   neděle 0:00, tedy kdykoliv, kdy by běžná expedice padla až na pondělí
//   a neděle ještě nezačala (pokrývá i čtvrtek večer před pátečním svátkem).
//   Neděle-svátek svoz ruší; pondělí-svátek posouvá běžnou expedici na úterý,
//   takže okno vůbec nevznikne (bez informace od klienta se svoz před svátkem
//   neslibuje).
//
// Marketingově je to nejzajímavější zpráva týdne — dřív šel páteční i sobotní
// balík až v pondělí — proto ji vedle termínu nese i věta `fastPromo()`.
//
// ROZMEZÍ TERMÍNU (manifest `shipping.deliverySpanDays`, SK = 1; zadání Tomáše
// 2026-09-29): doručení další pracovní den SK garantovat neumí, proto běžný
// termín není jedno datum, ale rozmezí „30. 9. – 1. 10." — od nejdřívějšího dne
// (tentýž výpočet jako na CZ) o `span` pracovních dní dál. Konec přeskakuje víkend
// i svátek jako expedice. Zvláštní závozy (`t.fast`) zůstávají na jednom datu:
// jejich den je dohoda s dopravcem, ne odhad.
//
// ZAČÁTEK TERMÍNU (manifest `shipping.deliveryStartDays`, CZ 1, SK 2; #572 V1,
// rozhodnutí Tomáše 14. 10. 2026): nejdřívější den doručení leží `start` pracovních
// dní za expedicí. SK doručuje od předání dopravci typicky druhý pracovní den
// (ostrá SK 25. 9.–8. 10.: 2 pd 86,7 %, 1 pd 4,4 %), proto „D+2 – D+3". Nativní
// datum Shoptetu vyhrává jen, když leží až za `start − 1` pracovními dny po
// expedici: u CZ (start 1) je to dosavadní „za expedicí", u SK nativní D+1
// posunutý začátek nepřebije.

import { isHoliday, isWorkday, dispatchEta, dispatchOn, beforeCutoff, cutoffHour, cutoffLabel } from './dispatchEta.js';
import SHOP from '../../config/shop.js';
import L from '../locale/cs/deliveryTimeline.js';

// Kolik pracovních dní leží konec rozmezí doručení za jeho začátkem (0 = jedno datum)
export const DELIVERY_SPAN_DAYS = SHOP.shipping.deliverySpanDays;
// Kolik pracovních dní za expedicí leží nejdřívější den doručení (CZ 1, SK 2)
export const DELIVERY_START_DAYS = SHOP.shipping.deliveryStartDays;

// Dopravci se zvláštním závozem. Rozsah = všechny metody dopravce (na adresu
// i na výdejní místo, u PPL i nadrozměr) podle zadání; kdyby závoz platil jen
// pro část, zúží se regex tady na jednom místě (např. /GLS na adresu/i).
// `tag` = krátký štítek VEDLE termínu (review Tomáše 2026-09-04: štítek pod
// hodnotou lámal osu na detailu na dva řádky → inline, proto nese jen
// podmínku dopravce; den říká samo datum „v sobotu 5. 9."). Od 2026-09-05
// kreslí štítek LOGO dopravce (`carrier` je zároveň klíč symbolu ve spritu
// logos.svg — logo.js `carrierTag`), text zůstává pro čtečky.
//
// Která pravidla v shopu platí, říká manifest (`shipping.fastRules`): svoz je
// dohoda KLIENTA s dopravcem ze skladu v Bílině, ne vlastnost dopravce — shop
// bez té dohody (SK) má pole prázdné a šablona nikde nic rychlejšího neslibuje.
const ALL_FAST_RULES = [
  { key: 'sat', carrier: 'GLS', re: /\bGLS\b/i, tag: L.withCarrier('GLS') },
  { key: 'sun', carrier: 'PPL', re: /\bPPL\b/i, tag: L.withCarrier('PPL') },
];
export const FAST_RULES = ALL_FAST_RULES.filter(function (r) {
  return SHOP.shipping.fastRules.indexOf(r.key) !== -1;
});
const RULE = (key) =>
  FAST_RULES.find(function (r) {
    return r.key === key;
  }) || null;
// štítek nejrychlejší metody ve výběru dopravy (krok 2, modal) — v okně ho nese
// rychlý dopravce vedle svého termínu, ostatní metody mají běžný termín
export const LABEL_FASTEST = L.fastest;
const LABEL_SAT_PROMO_LATER = L.satPromoLater;
const LABEL_SUN_PROMO_TODAY = L.sunPromoToday;
const LABEL_SUN_PROMO_LATER = L.sunPromoLater;
const LABEL_ORDER_SUN_TODAY = L.orderSunToday;
const LABEL_ORDER_SUN_LATER = L.orderSunLater;

// Sedí název metody na rychlého dopravce PRÁVĚ AKTIVNÍHO okna? Mimo okno
// (t.fast == null) vždy false — kdo se ptá, ptá se na konkrétní termín.
export function fastCarrier(t, name) {
  return !!(t && t.fast && t.fast.re.test(name || ''));
}

// Je rychlý dopravce mezi metodami v nabídce (názvy metod)? Bez něj se termín
// ani věta nikde neslibují (nadrozměr veze jen Fofr).
export function fastOffered(t, names) {
  if (!t || !t.fast) return false;
  return (names || []).some(function (n) {
    return t.fast.re.test(n || '');
  });
}

// Pořadí metod ve skupině v okně: rychlý dopravce PRVNÍ (nejrychlejší volba
// nahoře — zadání Tomáše 2026-09-04), zbytek v původním pořadí. Stabilní.
// Mimo okno vrací původní pořadí.
export function fastFirst(t, items, nameOf) {
  if (!t || !t.fast) return (items || []).slice();
  const fast = [];
  const rest = [];
  (items || []).forEach(function (it) {
    (fastCarrier(t, nameOf(it)) ? fast : rest).push(it);
  });
  return fast.concat(rest);
}

// Po cut-offu a o víkendu / svátku: „kdykoliv" nic neříkalo (zadání Tomáše
// 2026-09-05). „Dnes do půlnoci" je pravdivý slib — expedice je nejbližší
// pracovní den PO dnešku a do půlnoci se nezmění; skutečný poslední okamžik
// pro tentýž den expedice je až zítřejší cut-off, ale ten by na ose vedle
// „Odešleme zítra" četl zákazník jako datum expedice, ne objednávky.
export const LABEL_ORDER_MIDNIGHT = L.orderMidnight;
const LABEL_SHIP_TODAY = L.shipToday;
const LABEL_TOMORROW = L.tomorrow;
const LABEL_TODAY = L.today;

// 0 = neděle
const DAYS = L.days;

function startOfDay(d) {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

function addDays(d, n) {
  const out = new Date(d.getTime());
  out.setDate(out.getDate() + n);
  return out;
}

function sameDay(a, b) {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}

function nextWorkday(from) {
  let d = startOfDay(from);
  for (let i = 0; i < 14; i += 1) {
    d = addDays(d, 1);
    if (isWorkday(d)) return d;
  }
  return addDays(startOfDay(from), 1);
}

// `n`-tý pracovní den po `from` (n = 0 vrací `from` beze změny)
export function addWorkdays(from, n) {
  let d = startOfDay(from);
  for (let left = n; left > 0; left -= 1) d = nextWorkday(d);
  return d;
}

// Nejbližší den po `from`, kdy sobotní dopravce doručuje: pondělí až sobota
// mimo svátky (neděle ne, svátek v sobotu ne).
// Exportované i pro stránku Stav objednávky (orderStatusModel.estimateOf): odhad
// u zásilky předané GLS musí sedět s termínem, který slíbil nákup.
export function nextSaturdayCarrierDay(from) {
  let d = startOfDay(from);
  for (let i = 0; i < 14; i += 1) {
    d = addDays(d, 1);
    if (d.getDay() !== 0 && !isHoliday(d)) return d;
  }
  return addDays(startOfDay(from), 1);
}

// „25.8.2026" / „25. 8. 2026" / „25.8." → Date (rok doplní `now`). Neplatné → null.
export function parseNativeDate(text, now) {
  const m = String(text || '').match(/(\d{1,2})\s*\.\s*(\d{1,2})\s*\.\s*(\d{4})?/);
  if (!m) return null;
  const ref = now || new Date();
  const year = m[3] ? parseInt(m[3], 10) : ref.getFullYear();
  const d = new Date(year, parseInt(m[2], 10) - 1, parseInt(m[1], 10));
  if (isNaN(d.getTime())) return null;
  // bez roku: datum v minulosti znamená přelom roku
  if (!m[3] && d < startOfDay(ref)) d.setFullYear(year + 1);
  return d;
}

/**
 * Nativní text termínu (`div.delivery-time`, „27.8.2026") srovnaný s osou. Shoptet
 * zná jednu uzávěrku pro všechny dny (`/admin/zobrazeni-zbozi/` 15:00), takže po
 * dřívější páteční uzávěrce (14:30, manifest `shipping.dispatchCutoff`) slibuje
 * doručení o den dřív, než balík odejde. Kde osa nativní datum přebije
 * (`deliveryTimeline`: nativní den neleží za expedicí), vrací text s jejím dnem
 * v nativním tvaru; jinak text beze změny. Pro místa, která nativní termín
 * vypisují doslova (košík krok 1, rychlý náhled upsellu).
 * @param {string} text
 * @param {Date} [now]
 * @returns {string}
 */
export function nativeDeliveryText(text, now) {
  const ref = now || new Date();
  const native = parseNativeDate(text, ref);
  if (!native) return text;
  const d = deliveryTimeline(ref, native).deliverDate;
  if (sameDay(d, native)) return text;
  return String(text).replace(/(\d{1,2})\s*\.\s*(\d{1,2})\s*\.\s*(\d{4})?/, function (all, day, month, year) {
    return d.getDate() + '.' + (d.getMonth() + 1) + '.' + (year ? d.getFullYear() : '');
  });
}

// „24. 8." (bez roku, mezera za tečkou — znění prototypu)
export function shortDate(d) {
  return d.getDate() + '. ' + (d.getMonth() + 1) + '.';
}

// „30. 9. – 1. 10." — rozmezí dnů doručení. Datum drží pevné mezery, aby se
// řádek zalomil nejvýš u pomlčky, ne uprostřed „1. 10.".
export function rangeLabel(from, to) {
  const glue = (d) => shortDate(d).replace(/ /g, '\u00A0');
  return L.range(glue(from), glue(to));
}

// „dnes 31. 8." / „zítra 22. 8." / „v pondělí 24. 8."
// Sdílené i mimo osu doručení: termín vyzvednutí na prodejně (shippingModal.js,
// checkoutPage.js) se píše ve stejném tvaru jako termín doručení — zadání
// Tomáše 2026-08-31. Den doručení na dnešek nikdy nevyjde (leží za expedicí),
// termín vyzvednutí ano, proto větev „dnes".
export function relativeDayLabel(d, today) {
  const ref = today || startOfDay(new Date());
  if (sameDay(d, ref)) return LABEL_TODAY + ' ' + shortDate(d);
  if (sameDay(d, addDays(ref, 1))) return LABEL_TOMORROW + ' ' + shortDate(d);
  return DAYS[d.getDay()] + ' ' + shortDate(d);
}

/**
 * @param {Date} [now]
 * @param {Date|null} [nativeDelivery] den doručení z nativního `div.delivery-time`
 * @param {number} [spanDays] délka rozmezí doručení v pracovních dnech (default
 *   manifest `shipping.deliverySpanDays`; parametr kvůli testům obou shopů)
 * @param {number} [startDays] začátek termínu v pracovních dnech po expedici
 *   (default manifest `shipping.deliveryStartDays`, CZ 1, SK 2)
 * @returns {{ order: string, ship: string, deliver: string, shipsToday: boolean,
 *            cutoff: string|null, shipDate: Date, deliverDate: Date, deliverDateTo: Date|null,
 *            countdown: {hours:number, minutes:number}|null,
 *            fast: null|{ key: string, carrier: string, re: RegExp, tag: string,
 *                         shipDate: Date, deliverDate: Date, shipDiffers: boolean,
 *                         order: string, ship: string, deliver: string,
 *                         countdown: {hours:number, minutes:number}|null, promo: string } }}
 *   `fast` = zvláštní závoz dopravce (FAST_RULES), jen když dává dřívější termín
 *   než běžný; jinak null. `fast.deliver` má vždy tvar se dnem („v sobotu 5. 9.",
 *   „v pondělí 8. 9."), i v pátek, kdy by relativní tvar dal „zítra": den je ta
 *   informace, kterou termín nese (review 2026-09-04). `fast.ship`/`fast.order`/
 *   `fast.countdown` = hodnoty kroků „Odešleme"/„Objednáte" pro rychlého
 *   dopravce (u GLS shodné s běžnými, u PPL neděle a půlnoční uzávěrka);
 *   `shipDiffers` říká, zda se den odeslání liší od běžné expedice.
 *   `cutoff` = dnešní uzávěrka jako čas („14:30") ve dni expedice, jinak null.
 *   `deliver` je s rozmezím (`spanDays` > 0) tvar „30. 9. – 1. 10." bez relativního
 *   dne; `deliverDate` je vždy začátek, `deliverDateTo` konec (null bez rozmezí).
 */
export function deliveryTimeline(now, nativeDelivery, spanDays, startDays) {
  const span = spanDays == null ? DELIVERY_SPAN_DAYS : spanDays;
  const start = startDays == null ? DELIVERY_START_DAYS : startDays;
  const ref = now ? new Date(now.getTime()) : new Date();
  const today = startOfDay(ref);
  const shipsToday = beforeCutoff(ref);
  // dnešní uzávěrka jako čas („14:30"), jen když se dnes ještě expeduje
  const cutoff = shipsToday ? cutoffLabel(today) : null;

  const shipDate = shipsToday ? today : nextWorkday(today);
  // nativní datum vyhrává, ale jen když leží až za expedicí (jinak by osa lhala),
  // u posunutého začátku (SK) až za `start − 1` pracovními dny po ní
  const native = nativeDelivery && !isNaN(nativeDelivery.getTime()) ? startOfDay(nativeDelivery) : null;
  const standard = addWorkdays(shipDate, start);
  const nativeFloor = addWorkdays(shipDate, start - 1);
  const deliverDate = native && native > nativeFloor ? native : standard;
  const deliverDateTo = span > 0 ? addWorkdays(deliverDate, span) : null;

  const countdown = shipsToday ? countdownTo(ref, today, cutoffHour(today)) : null;
  const order = shipsToday ? L.orderToday(cutoff) : LABEL_ORDER_MIDNIGHT;
  const ship = shipsToday ? LABEL_SHIP_TODAY : relativeDayLabel(shipDate, today);

  // Zvláštní závoz jen tam, kde by běžný kurýr vezl hned další pracovní den po
  // expedici (zboží skladem — nativní datum nic neposunulo). Neskladové zboží
  // s pozdějším nativním termínem ho nedostane: naskladnění řídí Shoptet, ne
  // dopravce.
  let fast = null;
  const satRule = RULE('sat');
  const sunRule = RULE('sun');
  if (deliverDate.getTime() <= standard.getTime() && (satRule || sunRule)) {
    // GLS sobota: expedice v pátek → sobotní dopravce má den dřív
    const sat = satRule ? nextSaturdayCarrierDay(shipDate) : null;
    if (sat && sat < deliverDate) {
      fast = {
        rule: satRule,
        shipDate: shipDate,
        deliverDate: sat,
        order: order,
        ship: ship,
        countdown: countdown,
        promo: (shipsToday ? L.satPromoToday(cutoff) : LABEL_SAT_PROMO_LATER) + shortDate(sat),
      };
    } else if (sunRule && !shipsToday && shipDate.getDay() === 1) {
      // PPL neděle: běžná expedice padla až na pondělí; neděle před ním ještě
      // nezačala a není svátek → svoz v neděli, doručení v pondělí
      const sunday = addDays(shipDate, -1);
      if (ref < sunday && !isHoliday(sunday) && shipDate < deliverDate) {
        const saturdayNow = sameDay(ref, addDays(sunday, -1));
        fast = {
          rule: sunRule,
          shipDate: sunday,
          deliverDate: shipDate,
          order: saturdayNow ? LABEL_ORDER_SUN_TODAY : LABEL_ORDER_SUN_LATER,
          ship: relativeDayLabel(sunday, today),
          countdown: saturdayNow ? countdownTo(ref, today, 24) : null,
          promo: (saturdayNow ? LABEL_SUN_PROMO_TODAY : LABEL_SUN_PROMO_LATER) + shortDate(shipDate),
        };
      }
    }
  }
  if (fast) {
    fast.key = fast.rule.key;
    fast.carrier = fast.rule.carrier;
    fast.re = fast.rule.re;
    fast.tag = fast.rule.tag;
    fast.shipDiffers = !sameDay(fast.shipDate, shipDate);
    fast.deliver = DAYS[fast.deliverDate.getDay()] + ' ' + shortDate(fast.deliverDate);
    delete fast.rule;
  }

  return {
    order: order,
    ship: ship,
    deliver: deliverDateTo ? rangeLabel(deliverDate, deliverDateTo) : relativeDayLabel(deliverDate, today),
    shipsToday: shipsToday,
    cutoff: cutoff,
    shipDate: shipDate,
    deliverDate: deliverDate,
    deliverDateTo: deliverDateTo,
    countdown: countdown,
    fast: fast,
  };
}

/**
 * Expedice na kartě výpisu („Expedujeme v neděli") a v modalu „Přidáno do
 * košíku" („odesíláme v neděli"). Základ je pravidlo skladu (`dispatchEta`,
 * uzávěrka 15:00, v pátek 14:30, se svátky), ale zvláštní závoz, který ODESÍLÁ dřív než sklad
 * (`t.fast.shipDiffers` — dnes jen nedělní svoz PPL), posune den expedice na
 * den svozu. Zadání Tomáše 2026-09-19: karta v sobotu hlásila „Expedujeme
 * v pondělí", zatímco osa detailu, promo pruh i pokladna slibují odeslání
 * v neděli a doručení v pondělí. GLS sobota odesílá týž den jako sklad, na kartě
 * nic nemění.
 *
 * Nabídku dopravců karta nezná (detail ji čte ze stránky možností dopravy,
 * `fastOffered`), takže den svozu platí za PPL. Bez PPL je na ostré jen zboží
 * nad 30 kg (veze ho Fofr; 22 z 3 090 skladových variant, snapshot 11. 9.) —
 * tam karta slíbí neděli a detail drží pondělí. Vědomý kompromis: hmotnost
 * karta nemá a 99 % skladových položek PPL veze.
 *
 * @param {Date} [now]
 * @returns {{ label: string, when: string, days: number }} tvar jako `dispatchEta`
 */
export function listingDispatch(now) {
  const ref = now ? new Date(now.getTime()) : new Date();
  const t = deliveryTimeline(ref, null);
  if (t.fast && t.fast.shipDiffers) return dispatchOn(t.fast.shipDate, ref);
  return dispatchEta(ref);
}

// Odpočet z `ref` do `hour` téhož dne (hodiny desetinně, 14.5 = 14:30, 24 = půlnoc);
// nikdy záporný.
function countdownTo(ref, today, hour) {
  const cut = new Date(today.getTime());
  cut.setHours(0, Math.round(hour * 60), 0, 0);
  const mins = Math.max(0, Math.round((cut - ref) / 60000));
  return { hours: Math.floor(mins / 60), minutes: mins % 60 };
}

// Marketingová věta ke zvláštnímu závozu, nebo null mimo okno. GLS: „Objednejte
// do 14:30 a s GLS máte balík už zítra, v sobotu 5. 9." (pátek před cut-offem)
// / „Odešleme v pátek a s GLS máte balík už v sobotu 5. 9." (čtvrtek po
// cut-offu). PPL: „Objednejte dnes do půlnoci, odešleme v neděli a s PPL máte
// balík už v pondělí 8. 9." (sobota) / „Objednejte do sobotní půlnoci, …"
// (pátek po cut-offu). Kdo ji kreslí (detail, košík, krok 2, modal), musí mít
// dopravce v nabídce (`fastOffered`) — bez něj by věta lhala. shortDate končí
// tečkou („5. 9."), věta další nepřidává.
export function fastPromo(t) {
  return t && t.fast ? t.fast.promo : null;
}

// „2 h 05 min"
export function countdownLabel(cd) {
  if (!cd) return '';
  return cd.hours + ' ' + L.hours + ' ' + String(cd.minutes).padStart(2, '0') + ' ' + L.minutes;
}
